import { z } from "zod";

export const TOAST_TESTER_TYPES = [
  "default",
  "success",
  "error",
  "info",
  "warning",
  "loading",
  "promise"
] as const;

export type ToastTesterType = (typeof TOAST_TESTER_TYPES)[number];

export const MAX_TOAST_TESTER_COUNT = 5;

export const toastTesterConfigSchema = z.object({
  actionLabel: z.string().trim().min(1, "Action label is required"),
  cancelLabel: z.string().trim().min(1, "Cancel label is required"),
  count: z.number().int().min(1).max(MAX_TOAST_TESTER_COUNT),
  description: z.string().trim(),
  durationSeconds: z.number().min(1).max(60),
  isPersistent: z.boolean(),
  shouldPromiseReject: z.boolean(),
  title: z.string().trim().min(1, "Title is required"),
  type: z.enum(TOAST_TESTER_TYPES),
  withAction: z.boolean(),
  withCancel: z.boolean()
});

export type ToastTesterConfig = z.infer<typeof toastTesterConfigSchema>;

/**
 * Baseline config every preset extends, so presets only declare what differs.
 */
export const DEFAULT_TOAST_TESTER_CONFIG: ToastTesterConfig = {
  actionLabel: "Undo",
  cancelLabel: "Cancel",
  count: 1,
  description: "This is a test toast from Local Devtools.",
  durationSeconds: 4,
  isPersistent: false,
  shouldPromiseReject: false,
  title: "Test toast",
  type: "default",
  withAction: false,
  withCancel: false
};

export type ToastTesterPreset = {
  label: string;
  config: ToastTesterConfig;
};

/**
 * Shortcuts that reproduce toasts the app raises for real, so styling and
 * stacking can be checked without triggering the underlying flow.
 */
export const TOAST_TESTER_PRESETS: readonly ToastTesterPreset[] = [
  {
    config: {
      ...DEFAULT_TOAST_TESTER_CONFIG,
      description: "The note has been removed from your list.",
      title: "Note deleted",
      type: "success"
    },
    label: "Success"
  },
  {
    config: {
      ...DEFAULT_TOAST_TESTER_CONFIG,
      description:
        "The notification could not be marked as read. Please refresh and try again.",
      title: "Mark as Read Failed",
      type: "error"
    },
    label: "Error"
  },
  {
    config: {
      ...DEFAULT_TOAST_TESTER_CONFIG,
      actionLabel: "View",
      description: "Acme Ltd — 14:30",
      title: "Callback in 20 minutes - Acme Ltd",
      withAction: true
    },
    label: "Notification"
  },
  {
    config: {
      ...DEFAULT_TOAST_TESTER_CONFIG,
      actionLabel: "Update",
      description:
        "Please update search before sending the quote to the customer.",
      isPersistent: true,
      title: "Unsaved changes",
      withAction: true
    },
    label: "Persistent action"
  },
  {
    config: {
      ...DEFAULT_TOAST_TESTER_CONFIG,
      count: MAX_TOAST_TESTER_COUNT,
      title: "Stacked toast"
    },
    label: "Stack"
  }
];
